import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { MessageSquarePlus, X, Send, Bug, Lightbulb, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: 'bug', label: 'Bug', icon: Bug, color: '#EE2130' },
  { key: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: '#F4621C' },
  { key: 'question', label: 'Question', icon: HelpCircle, color: '#2E6CF5' },
];

const FloatingComment = ({ studentInfo }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/comments`, {
        student_name: `${studentInfo?.first_name || ''} ${studentInfo?.last_name || ''}`.trim() || 'Anonyme',
        student_number: studentInfo?.student_number || '',
        category,
        message: message.trim(),
        page: location.pathname,
      });
      toast.success('Commentaire envoyé, merci !');
      setMessage('');
      setOpen(false);
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-6 z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform print:hidden"
          style={{ backgroundColor: '#002768' }}
          data-testid="floating-comment-button"
          title="Envoyer un commentaire"
        >
          <MessageSquarePlus className="w-5 h-5" />
        </button>
      )}

      {/* Comment form */}
      {open && (
        <div className="fixed bottom-20 right-6 z-50 w-80 sm:w-96 print:hidden" data-testid="comment-form-panel">
          <div className="bg-white rounded-xl shadow-2xl border-2 overflow-hidden" style={{ borderColor: '#002768' }}>
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between text-white" style={{ backgroundColor: '#002768' }}>
              <span className="font-semibold text-sm">Envoyer un commentaire</span>
              <button onClick={() => setOpen(false)} className="hover:opacity-70">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Category picker */}
              <div className="flex gap-2">
                {CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const active = category === cat.key;
                  return (
                    <button
                      key={cat.key}
                      onClick={() => setCategory(cat.key)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium border-2 transition-all"
                      style={{
                        borderColor: cat.color,
                        backgroundColor: active ? cat.color : 'transparent',
                        color: active ? 'white' : cat.color,
                      }}
                      data-testid={`comment-cat-${cat.key}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {cat.label}
                    </button>
                  );
                })}
              </div>

              {/* Message */}
              <textarea
                placeholder="Décrivez votre commentaire..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none text-sm resize-none"
                style={{ '--tw-ring-color': '#2E6CF5' }}
                data-testid="comment-message"
                autoFocus
              />

              {/* Footer */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {studentInfo?.first_name} {studentInfo?.last_name}
                </span>
                <button
                  onClick={handleSubmit}
                  disabled={!message.trim() || sending}
                  className="flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                  style={{ backgroundColor: '#24B219' }}
                  data-testid="comment-submit"
                >
                  {sending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingComment;
